import { jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import { lifecycle, compose, branch } from "recompose";
import { withRouter, Link } from "react-router-dom";
import {
  MenuItem,
  MenuDivider,
  Tooltip,
  KeyCombo,
  ContextMenu,
  Menu,
  Classes,
  Icon
} from "@blueprintjs/core";
import {
  startCase,
  omit,
  isNumber,
  flatMap,
  isArray,
  isString,
  noop
} from "lodash-es";
import fuzzysearch from "fuzzysearch";
import classNames from "classnames";
export function MenuItemLink({ text, onClick, icon, navTo, active, disabled }) {
  if (disabled) {
    return /* @__PURE__ */ jsx("li", { className: Classes.POPOVER_DISMISS, children: /* @__PURE__ */ jsx(MenuItem, { icon, disabled: true, text }) });
  }
  const handleLinkClick = (e) => {
    e.target.closest(`.${Classes.POPOVER_DISMISS}`).click();
  };
  return /* @__PURE__ */ jsx("li", { className: Classes.POPOVER_DISMISS, onClick, children: /* @__PURE__ */ jsxs(
    Link,
    {
      onClick: handleLinkClick,
      to: navTo,
      className: classNames(Classes.MENU_ITEM, {
        [Classes.ACTIVE]: active,
        [Classes.INTENT_PRIMARY]: active
      }),
      children: [
        icon && /* @__PURE__ */ jsx(Icon, { icon }),
        /* @__PURE__ */ jsx("div", { className: "bp3-text-overflow-ellipsis bp3-fill", children: text })
      ]
    }
  ) });
}
export const EnhancedMenuItem = compose(
  lifecycle({
    componentDidMount: function() {
      const { didMount = noop, className } = this.props;
      didMount({ className });
    },
    componentWillUnmount: function() {
      const { willUnmount = noop, className } = this.props;
      willUnmount({ className });
    }
  }),
  branch(({ navTo }) => navTo, withRouter)
)(function({
  navTo,
  context,
  staticContext,
  didMount,
  willUnmount,
  ...props
}) {
  let MenuItemComp = MenuItem;
  if (navTo) {
    MenuItemComp = MenuItemLink;
  }
  return /* @__PURE__ */ jsx(
    MenuItemComp,
    {
      popoverProps: {
        autoFocus: false
      },
      ...navTo && { navTo },
      ...props,
      onClick: props.onClick ? (...args) => {
        return props.onClick(...args, context);
      } : void 0
    }
  );
});
function fnu(...args) {
  return args.find((v) => v !== void 0);
}
export const tickMenuEnhancer = (def) => {
  const out = { ...def };
  if (out.checked !== void 0) {
    out.icon = out.checked ? "small-tick" : "blank";
  }
  return out;
};
export const commandMenuEnhancer = (commands, config = {}) => (def, context) => {
  const cmdId = typeof def === "string" ? def : def.cmd;
  let item = typeof def === "string" ? { cmd: def } : { ...def };
  const useTicks = fnu(item.useTicks, config.useTicks);
  delete item.useTicks;
  if (cmdId && commands[cmdId] && def.divider === void 0) {
    const command = commands[cmdId];
    const { isActive, isDisabled, isHidden } = command;
    const toggles = isActive !== void 0;
    item.hidden = fnu(item.hidden, isHidden);
    item.disabled = fnu(item.disabled, isDisabled);
    item.key = item.key || cmdId;
    item.submenu = item.submenu || command.submenu;
    item.component = item.component || command.component;
    if (toggles) {
      if (useTicks) {
        item.text = item.text || command.shortName || command.name;
        item.checked = item.checked || isActive;
      } else {
        item.text = item.text || (isActive ? command.name : command.inactiveName || command.name);
        item.icon = item.icon || (isActive ? command.icon : command.inactiveIcon || command.icon);
      }
    } else {
      item.text = item.text || command.name;
      item.icon = item.icon || command.icon;
    }
    item.hotkey = item.hotkey || command.hotkey;
    if (!item.onClick) {
      item.onClick = (event) => command.execute({
        event,
        context,
        menuItem: item,
        viaMenu: true
      });
    }
  } else if (cmdId && !commands[cmdId]) {
    item.text = item.text || startCase(cmdId);
    item.disabled = true;
  }
  if (config.omitIcons) {
    item.icon = void 0;
  }
  if (config.forceIconAlignment !== false) {
    item.icon = item.icon || "blank";
  }
  if (useTicks) {
    item = tickMenuEnhancer(item);
  }
  return item;
};
const ident = (x) => x;
const dividerShorthandEnhancer = (def) => typeof def === "string" && def.startsWith("--") ? { divider: def.substr(2) } : def;
const unwantedAttrs = [
  "isSimpleText",
  "justText",
  "submenu",
  "component",
  "hotkey",
  "changingProps",
  "showInSearchMenu",
  "hideFromMenuSearch",
  "key"
];
export const DynamicMenuItem = ({
  def,
  enhancers = [ident],
  context,
  doNotEnhanceTopLevelItem
}) => {
  if (React.isValidElement(def)) return def;
  const item = [
    dividerShorthandEnhancer,
    ...doNotEnhanceTopLevelItem ? [ident] : enhancers
  ].reduce((v, f) => f(v, context), def);
  let out;
  if (item.divider !== void 0) {
    out = /* @__PURE__ */ jsx(
      MenuDivider,
      {
        ...item.divider ? { title: item.divider, className: item.className } : {}
      }
    );
  } else {
    const ItemComponent = item.component || EnhancedMenuItem;
    out = /* @__PURE__ */ jsx(
      ItemComponent,
      {
        ...omit(item, unwantedAttrs),
        context,
        icon: item.icon || item.iconName,
        labelElement: item.hotkey && /* @__PURE__ */ jsx(
          KeyCombo,
          {
            minimal: true,
            combo: Array.isArray(item.hotkey) ? item.hotkey[0] : item.hotkey
          }
        ),
        text: item.text,
        children: item.submenu ? item.submenu.filter(ident).map((def2, index) => /* @__PURE__ */ jsx(DynamicMenuItem, { ...{ def: def2, enhancers, context } }, index)) : void 0
      }
    );
  }
  if (item.disabled && typeof item.disabled === "string") {
    item.tooltip = item.disabled;
  }
  if (item.tooltip) {
    out = /* @__PURE__ */ jsx(Tooltip, { content: item.tooltip, children: out });
  }
  return item.hidden ? null : out;
};
export const createDynamicMenu = (menuDef, enhancers, context) => {
  if (menuDef instanceof Array) {
    return menuDef.map((def, index) => /* @__PURE__ */ jsx(DynamicMenuItem, { ...{ def, enhancers, context } }, index));
  } else {
    return /* @__PURE__ */ jsx(DynamicMenuItem, { ...{ def: menuDef, enhancers, context } });
  }
};
export const createDynamicBarMenu = (topMenuDef, enhancers, context) => {
  return topMenuDef.map((topLevelItem) => {
    const def = { ...topLevelItem };
    if (def.submenu) {
      def.submenu = def.submenu.map((subdef, index) => /* @__PURE__ */ jsx(DynamicMenuItem, { def: subdef, ...{ enhancers, context } }, index));
    }
    return def;
  });
};
export const createCommandMenu = (menuDef, commands, config, context) => {
  return createDynamicMenu(
    menuDef,
    [commandMenuEnhancer(commands, config)],
    context
  );
};
export const createCommandBarMenu = (menuDef, commands, config, context) => {
  return createDynamicBarMenu(
    menuDef,
    [commandMenuEnhancer(commands, config)],
    context
  );
};
export function showCommandContextMenu(menuDef, commands, config, event, onClose, context) {
  return showContextMenu(
    menuDef,
    [commandMenuEnhancer(commands, config)],
    event,
    onClose,
    context
  );
}
export const createMenu = createDynamicMenu;
export function showContextMenu(menuDef, enhancers, event, onClose, context, menuComp = Menu) {
  menuDef = filterMenuForCorrectness(menuDef);
  if (!menuDef) return;
  const MenuComponent = menuComp;
  event.persist && event.persist();
  ContextMenu.show(
    /* @__PURE__ */ jsx(MenuComponent, { children: createDynamicMenu(menuDef, enhancers, context) }),
    { left: event.clientX, top: event.clientY },
    onClose
  );
  event.stopPropagation && event.stopPropagation();
  event.preventDefault && event.preventDefault();
}
function filterMenuForCorrectness(menuDef) {
  return menuDef && menuDef.length && menuDef.filter(ident);
}
export function getStringFromReactComponent(comp) {
  if (!comp) return "";
  if (isString(comp) || isNumber(comp)) return comp;
  if (comp.props?.text) {
    return getStringFromReactComponent(comp.props.text);
  }
  const { children } = comp.props || {};
  if (!children) return "";
  if (isArray(children))
    return flatMap(children, getStringFromReactComponent).join("");
  if (isString(children)) return children;
  if (children.props) {
    return getStringFromReactComponent(children.props);
  }
}
export function doesSearchValMatchText(searchVal, justText) {
  return fuzzysearch(
    searchVal ? searchVal.toLowerCase() : "",
    justText ? justText.toLowerCase() : ""
  );
}
export const MenuItemWithTooltip = ({ tooltip, ...rest }) => {
  let out = /* @__PURE__ */ jsx(MenuItem, { ...rest });
  if (tooltip) {
    out = /* @__PURE__ */ jsx(Tooltip, { content: tooltip, children: out });
  }
  return out;
};
